import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import FileUpload from '@alefeducation/file-upload';
import { FilePreview, RenderingUnsupported } from '@alefeducation/file-preview';
import { Button, ThemeProvider } from '@alefeducation/ui-elements-library-core';
import { PlusCircleSolid } from '@alefeducation/ui-elements-library-icons';
import Replace from '../common/Icons/Replace';
import StudentPopup from '../common/StudentPopup/StudentPopup';
import getLocaleDate from '../../utils/getLocaleDate';
import {
  ButtonContainer,
  FlexContainer,
} from '../common/AssignmentPageLayout/AssignmentPageLayout';
import { useLanguage } from '../../utils/language';
import fireNotification from '../../utils/fireNotification';
import { errorTypes, uploadTypes } from '../common/FileUpload/constants';
import DownloadAttachmentButton from '../AssignmentSubmissionEvaluate/DownloadAttachmentButton';
import getFileRendererSize from '../../utils/fileRendererSize';
import {
  disablePointerEventsOnPDFRenderer,
  enablePointerEventsOnPDFRenderer,
} from '../../utils/pdfRendererStylesUtil';
import uploadResultToAttachmentInput from '../../utils/uploadResultToAttachmentInput';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import assignmentTypes from '../../utils/assignmentTypes';
import useLDClientWrapper from '../../featureToggles/useLDClientWrapper';
import FeatureFlags from '../../featureToggles/FeatureFlags';
import UploadedFileDetails from './UploadedFileDetails';
import {
  getAttachmentGeneralFileType,
  getAttachmentRenderableFileType,
  getPreviewFileURL,
  shouldCheckForFileExistance,
} from './attachment.utils';
import {
  ATTACHMENT_CONTEXT_ACTIONS,
  CONVERSION_STATUS,
  useAssignmentAttachmentDispatch,
  useAssignmentAttachmentState,
} from './AssignmentAttachmentContext';
import { assignmentSubmissionProps } from './helpers';
import { AssignmentComment } from './AssignmentComment';
import useSubmission from './useSubmission';

const SubmissionPanel = ({
  submission,
  setUpdatingCommentComplete,
  isSaveButtonClicked,
  isSubmitButtonClicked,
  setIsSubmitButtonClicked,
  isSubmitted,
  dueOn,
  setIsFormModified,
  attachmentRequired,
  commentRequired,
  onSaveOrSubmit,
  shouldHideButtons,
  submissionHandler,
  onAssignmentLocked,
  onLessonLocked,
}) => {
  const { t, i18n, dir } = useTranslation();
  const {
    assignmentParams: {
      assignmentId,
      assignmentInstanceId,
      mloId,
      instructionalPlanId,
      classId,
      contentId,
      isGradableItemsLockedByDefault,
    },
    assignmentData: { assignmentType },
    methods: { refetchStudentAssignment },
  } = useContext(AssignmentContext);

  // ToDo:i18n cleanup with i18n.
  const { lang, langCode } = useLanguage();
  const ldClient = useLDClientWrapper();
  const isAzureFileStorageEnabled = ldClient.variation(
    FeatureFlags.ENABLE_AZURE_FILE_STORAGE,
    false,
  );
  const inputRef = useRef(null);

  const initAttachment =
    get(submission, 'response.attachment') ||
    get(submission, 'attachment') ||
    null;
  const assignmentTypeForPendoClass = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const initComment = get(submission, 'comment') || '';
  const initCommentPreviewActive = initComment !== '';
  const [comment, setComment] = useState(initComment);
  const [isAddCommentActive, setAddCommentActive] = useState(false);
  const [isCommentPreviewActive, setCommentPreviewActive] = useState(
    initCommentPreviewActive,
  );
  const [shouldShowRemovePrompt, setRemovePrompt] = useState(false);

  const toggleRemovePrompt = () => {
    setRemovePrompt(!shouldShowRemovePrompt);
  };

  const {
    conversionStatus,
    fileType: selectedFileType,
    attachment,
    uploadError,
  } = useAssignmentAttachmentState();
  const attachmentContextDispatch = useAssignmentAttachmentDispatch();
  const teacherResponseAttachment = get(submission, 'response.attachment');
  const isSubmissionReturned = get(submission, 'status') === 'RETURNED';
  const shouldRenderResponseAttachment =
    isSubmissionReturned && teacherResponseAttachment;
  const hasFileFormatError =
    uploadError && uploadError.errorType === errorTypes.fileFormatError;

  const [fileUploadInstanceId, setFileUploadInstanceId] = useState(1);

  const closeSubmitModal = useCallback(() => setIsSubmitButtonClicked(false));
  const openSubmitModal = useCallback(() => setIsSubmitButtonClicked(true));

  const { saveOrSubmitSubmission } = useSubmission({
    submission,
    classId,
    contentId:
      assignmentType === assignmentTypes.ALEF
        ? contentId
        : assignmentInstanceId,
    mloId,
    instructionalPlanId,
    assignmentType,
    onAssignmentLocked,
    onLessonLocked,
  });

  const isConversionSuccessful = [
    CONVERSION_STATUS.finished,
    CONVERSION_STATUS.idle,
  ].includes(conversionStatus);

  const shouldRenderFile = () => {
    if (!attachment) {
      return false;
    }

    return uploadError
      ? uploadError.errorType === errorTypes.fileFormatError
      : isConversionSuccessful;
  };

  useEffect(
    () => {
      if (shouldHideButtons)
        submissionHandler({
          onSave: async () => {
            const operation = 'SAVE';
            const input = {
              assignmentId,
              referrerId: assignmentInstanceId || mloId,
              instructionalPlanId,
              attachment: uploadResultToAttachmentInput(attachment),
              comment,
              operation,
              isGradableItemsLockedByDefault,
            };
            await saveOrSubmitSubmission(input, operation, onSaveOrSubmit);

            // refetch student assignment on save
            await refetchStudentAssignment();
          },
          onSubmit: () => {
            openSubmitModal();
          },
        });
    },
    [comment, attachment],
  );

  const handleFormSubmit = useCallback(
    async () => {
      const operation = isSubmitButtonClicked ? 'SUBMIT' : 'SAVE';
      const input = {
        assignmentId,
        referrerId: assignmentInstanceId || mloId,
        instructionalPlanId,
        attachment: uploadResultToAttachmentInput(attachment),
        comment,
        operation,
        isGradableItemsLockedByDefault,
      };

      await saveOrSubmitSubmission(input, operation, onSaveOrSubmit);
      closeSubmitModal();

      // refetch student assignment on submit
      await refetchStudentAssignment();
    },
    [
      isSubmitButtonClicked,
      assignmentInstanceId,
      attachment,
      comment,
      submission,
    ],
  );

  useEffect(
    () => {
      attachmentContextDispatch({
        type: ATTACHMENT_CONTEXT_ACTIONS.updateAttachment,
        payload: initAttachment,
      });
    },
    [initAttachment],
  );

  useEffect(
    () => {
      setUpdatingCommentComplete(comment.trim().length > 0);
    },
    [comment],
  );

  useEffect(
    () => {
      if (
        comment !== initComment ||
        (attachment && attachment.path) !==
          (initAttachment && initAttachment.path)
      )
        setIsFormModified(true);
      else setIsFormModified(false);
    },
    [comment, attachment],
  );

  useEffect(
    () => {
      if (isSaveButtonClicked) handleFormSubmit();
    },
    [isSaveButtonClicked, isSubmitButtonClicked],
  );

  useEffect(
    () => {
      const hasConversionError =
        uploadError &&
        uploadError.errorType === errorTypes.conversionError &&
        attachment;

      const shouldShowFileFormatError = hasFileFormatError && attachment;

      if (hasConversionError && selectedFileType !== 'DOCUMENT') {
        fireNotification(uploadError.errorType, langCode);
        return;
      }

      if (shouldShowFileFormatError) {
        fireNotification(uploadError.errorType, langCode);
      }
    },
    [uploadError],
  );

  const setPreviewActive = () => {
    console.log('setPreviewActive');
    setAddCommentActive(false);
    setCommentPreviewActive(comment.trim().length > 0);
  };

  const setUpdateCommentActive = () => {
    setAddCommentActive(true);
    setCommentPreviewActive(false);
  };

  const setAutoHeight = event => {
    const { target } = event;
    target.style.height = `0px`;
    target.style.height = `${target.scrollHeight}px`;
  };

  const onCommentChange = ({ target }) => {
    setAutoHeight({ target });
    setComment(target.value);
  };

  const submittedOn =
    submission &&
    submission.submittedOn &&
    getLocaleDate({ date: submission.submittedOn, language: i18n.language });

  const shouldShowUploadDetails =
    attachment &&
    (isConversionSuccessful ||
      (uploadError && selectedFileType === 'DOCUMENT'));

  const canReplaceFile = shouldShowUploadDetails && !isSubmitted;

  const isDocumentAttachment =
    attachment && getAttachmentGeneralFileType(attachment) === 'document';
  return (
    <ThemeProvider>
      {isSubmitButtonClicked && (
        <StudentPopup
          title={t('Submit Assignment')}
          message={t(
            'Are you sure you want to submit your assignment? You cannot update it later.',
          )}
          onClickConfirm={handleFormSubmit}
          onClickCancel={closeSubmitModal}
          buttonTextConfirm={t('Submit')}
          buttonTextCancel={t('Cancel')}
          isRTL={dir === 'rtl'}
        />
      )}
      <FlexContainer className="assignmentSubmission-container">
        <AssignmentComment
          commentRequired={commentRequired}
          attachmentRequired={attachmentRequired}
          isSubmitted={isSubmitted}
          isAddCommentActive={isAddCommentActive}
          comment={comment}
          isCommentPreviewActive={isCommentPreviewActive}
          onCommentChange={onCommentChange}
          setAutoHeight={setAutoHeight}
          setPreviewActive={setPreviewActive}
          setUpdateCommentActive={setUpdateCommentActive}
        />
        {!isSubmitted && (
          <ButtonContainer>
            {canReplaceFile ? (
              <Button
                data-testid="replaceAttachedFile"
                disabled={isSubmitted}
                size="lg"
                variant={'secondary'}
                className={`data-analytics-${assignmentTypeForPendoClass}-student-assignment-submission-page-replace-file`}
                icon={
                  <Replace
                    color="#000"
                    data-testid="uploader-attachment-icon"
                    size={18}
                  />
                }
                onClick={() => {
                  if (inputRef && inputRef.current) {
                    inputRef.current.click();
                  }
                }}
              >
                {t('Replace File')}
              </Button>
            ) : null}

            {((commentRequired && !attachmentRequired) ||
              (!commentRequired && attachmentRequired)) &&
            !isAddCommentActive &&
            !isCommentPreviewActive &&
            !isSubmitted ? (
              <Button
                variant={'secondary'}
                size={'lg'}
                icon={<PlusCircleSolid fontSize={18} />}
                data-testid="addCommentButton"
                className={`data-analytics-${assignmentTypeForPendoClass}-student-assignment-submission-page-add-comment`}
                onClick={() => setAddCommentActive(true)}
              >
                {t('Add Comment')}
              </Button>
            ) : null}
          </ButtonContainer>
        )}
        {isSubmitted &&
          (shouldRenderResponseAttachment || submission.attachment) &&
          isDocumentAttachment && (
            <DownloadAttachmentButton
              attachment={
                shouldRenderResponseAttachment
                  ? teacherResponseAttachment
                  : submission.attachment
              }
            />
          )}
        {shouldShowUploadDetails ? (
          <UploadedFileDetails
            attachment={
              shouldRenderResponseAttachment
                ? teacherResponseAttachment
                : attachment
            }
            isSubmitted={isSubmitted}
            submittedOn={submittedOn}
            dueOn={dueOn}
            toggleRemovePrompt={toggleRemovePrompt}
          />
        ) : null}
        {!shouldRenderResponseAttachment &&
          attachmentRequired && (
            <FileUpload
              configs={{
                consumerId:
                  window.AssignmentAppConfig.fileService
                    .FILE_SERVICE_CONSUMER_ID,
                uploadType: uploadTypes.submission,
                configCode:
                  window.AssignmentAppConfig.fileService
                    .STUDENT_ASSIGNMENT_ATTACHMENT,
                videoFileDurationMins:
                  window.AssignmentAppConfig.VIDEO_FILE_DURATION_MINS,
                assignmentInstanceId: assignmentInstanceId || assignmentId,
                taskInfoPollInterval:
                  window.AssignmentAppConfig.TASK_INFO_POLL_INTERVAL,
                taskInfoPollTimeout:
                  window.AssignmentAppConfig.TASK_INFO_POLL_TIMEOUT,
                enableAzureFileStorage: isAzureFileStorageEnabled,
              }}
              key={`file-upload-instance-${fileUploadInstanceId}`}
              useAsHiddenInputFilePlaceholder={shouldShowUploadDetails}
              ref={inputRef}
              onRemove={() => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.removeAttachment,
                });
              }}
              onReset={() => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.updateAttachment,
                  payload: attachment,
                });
              }}
              onComplete={uploadResult => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.finishedUploading,
                  payload: uploadResult,
                });
              }}
              onTaskUpdate={taskInfo => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.conversionStatusChange,
                  payload: taskInfo,
                });
              }}
              onSelectFile={() => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.removeAttachment,
                });
              }}
              onStart={payload => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.startedUploading,
                  payload,
                });
              }}
              onError={error => {
                attachmentContextDispatch({
                  type: ATTACHMENT_CONTEXT_ACTIONS.uploadError,
                  payload: error,
                });
              }}
              getHiddenInputDropAreaSize={getFileRendererSize}
              onDrag={disablePointerEventsOnPDFRenderer}
              onDragLeave={enablePointerEventsOnPDFRenderer}
              onDrop={enablePointerEventsOnPDFRenderer}
              language={{
                dir,
                lang,
                langCode: i18n.language,
              }}
            />
          )}
        {attachment &&
          selectedFileType === 'DOCUMENT' &&
          uploadError &&
          uploadError.errorType !== errorTypes.fileFormatError && (
            <RenderingUnsupported />
          )}

        {shouldRenderFile() && (
          <FilePreview
            checkForFileExistanceFirst={shouldCheckForFileExistance(attachment)}
            fileUrl={getPreviewFileURL(attachment)}
            fileType={getAttachmentRenderableFileType(attachment)}
            fluid={true}
            language={{
              dir,
              lang,
              langCode: i18n.language,
            }}
          />
        )}
        {shouldShowRemovePrompt && (
          <StudentPopup
            testId={'attachment-remove-prompt'}
            onClickConfirm={() => {
              setFileUploadInstanceId(fileUploadInstanceId + 1);
              toggleRemovePrompt();
              attachmentContextDispatch({
                type: ATTACHMENT_CONTEXT_ACTIONS.removeAttachment,
              });
            }}
            isRTL={dir === 'rtl'}
            onClickCancel={toggleRemovePrompt}
            buttonTextCancel={t('Cancel')}
            buttonTextConfirm={t('Remove')}
            message={t('Are you sure you want to remove the file submission?')}
            title={t('Remove Submission')}
          />
        )}
      </FlexContainer>
    </ThemeProvider>
  );
};

SubmissionPanel.defaultProps = {
  submission: null,
  onSaveOrSubmit: () => {},
};

SubmissionPanel.propTypes = assignmentSubmissionProps;

export default SubmissionPanel;
